import React, { useState } from 'react';
import { Mail, Eye, EyeOff, User, Phone } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import authService from '../services/authService';

interface SignUpFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState<SignUpFormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validações
      if (formData.password !== formData.confirmPassword) {
        throw new Error('As senhas não coincidem');
      }

      if (formData.password.length < 8) {
        throw new Error('A senha deve ter pelo menos 8 caracteres');
      }

      if (!formData.acceptTerms) {
        throw new Error('Você deve aceitar os termos de uso');
      }

      const response = await authService.signup({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });

      if (response.success) {
        console.log('SignUp successful:', response.data);
        // Redirecionar para página de login ou fazer login automático
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect');
        const signinUrl = redirect
          ? `/signin?message=Conta criada com sucesso! Faça login para continuar.&redirect=${encodeURIComponent(redirect)}`
          : '/signin?message=Conta criada com sucesso! Faça login para continuar.';
        window.location.href = signinUrl;
      } else {
        throw new Error(response.message || 'Erro ao criar conta');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta. Tente novamente.');
      console.error('SignUp error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src="/santogram_logo.png"
              alt="SantoGram"
              className="h-24 w-auto"
            />
          </div>
          <p className="text-brown-600">
            Crie sua conta e inicie sua jornada espiritual
          </p>
        </div>

        {/* SignUp Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200">
          <div className="mb-6 text-center">
            <h2 className="text-2xl font-display font-bold text-brown-800 mb-2">
              Criar Nova Conta
            </h2>
            <p className="text-brown-600 text-sm">
              Junte-se à nossa comunidade de fé
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div className="relative">
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Seu nome completo"
                label="Nome Completo"
                required
              />
              <User className="absolute right-3 top-[52px] transform -translate-y-1/2 w-5 h-5 text-brown-400 pointer-events-none" />
            </div>

            {/* Email Field */}
            <div className="relative">
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="seu@email.com"
                label="E-mail"
                required
              />
              <Mail className="absolute right-3 top-[52px] transform -translate-y-1/2 w-5 h-5 text-brown-400 pointer-events-none" />
            </div>

            {/* Phone Field */}
            <div className="relative">
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(11) 99999-9999"
                label="Telefone (Opcional)"
              />
              <Phone className="absolute right-3 top-[52px] transform -translate-y-1/2 w-5 h-5 text-brown-400 pointer-events-none" />
            </div>

            {/* Password Field */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Sua senha (min. 8 caracteres)"
                label="Senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[52px] transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Confirm Password Field */}
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirme sua senha"
                label="Confirmar Senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-[52px] transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Terms acceptance */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="acceptTerms"
                name="acceptTerms"
                checked={formData.acceptTerms}
                onChange={handleInputChange}
                className="w-4 h-4 text-gold-600 border-cream-300 rounded focus:ring-gold-500 mt-1"
                required
              />
              <label htmlFor="acceptTerms" className="text-sm text-brown-700">
                Eu aceito os{' '}
                <span className="cursor-pointer text-gold-600 hover:text-gold-700 font-medium">
                  Termos de Uso
                </span>{' '}
                e a{' '}
                <span className="cursor-pointer text-gold-600 hover:text-gold-700 font-medium">
                  Política de Privacidade
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Criando conta...
                </>
              ) : (
                'Criar Conta'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-cream-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-brown-500">ou</span>
              </div>
            </div>
          </div>

          {/* Sign in link */}
          <div className="text-center">
            <p className="text-sm text-brown-600">
              Já tem uma conta?{' '}
              <a href="/signin" className="text-gold-600 hover:text-gold-700 font-medium">
                Faça login
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-brown-500">
            Ao criar uma conta, você estará se juntando a uma comunidade de fé e
            receberá conteúdo espiritual enriquecedor.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;